import { useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';

/**
 * Asks the visitor to allow or deny an action the agent wants to take in the page they are
 * looking at — a `confirm` category client-provided tool (services/tools/clientTool.js),
 * shown while pendingJob.status === 'waiting_for_client'.
 *
 * Sibling to AskUserCard, and deliberately not the same component: that one collects an
 * ANSWER, this one grants PERMISSION. The embed surface is unattended (no admin is watching
 * to approve a gated tool), so the person whose account the action would affect is the
 * right and only approver — which is exactly why the arguments are rendered rather than
 * summarized. "Allow" has to mean allow this, not allow something.
 */

/** Falls back to the tool name when the customer's manifest gave no confirmPrompt, so a
 *  missing prompt degrades to something readable instead of an empty card. */
function promptFor(call) {
  if (call.confirmPrompt) return call.confirmPrompt;
  return `Allow the assistant to run "${call.name}" on this page?`;
}

function ArgumentList({ args }) {
  const entries = Object.entries(args || {});
  if (!entries.length) return null;
  return (
    <dl className="text-xs bg-[var(--app-border)]/20 rounded-md px-2 py-1.5 space-y-0.5">
      {entries.map(([name, value]) => (
        <div key={name} className="flex gap-2">
          <dt className="text-secondary-500 shrink-0">{name}</dt>
          <dd className="font-mono break-all">{typeof value === 'string' ? value : JSON.stringify(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function ClientToolConfirmCard({ call, onDecide }) {
  const [deciding, setDeciding] = useState(false);

  const decide = (allowed) => {
    if (deciding) return;
    setDeciding(true);
    onDecide(call, allowed);
  };

  return (
    <div className="max-w-[90%] rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-3 space-y-2">
      <p className="text-sm font-medium flex items-start gap-2">
        <ShieldAlert size={15} className="text-primary-600 shrink-0 mt-0.5" />
        <span>{promptFor(call)}</span>
      </p>
      <ArgumentList args={call.args} />
      <div className="flex gap-2">
        <Button size="sm" disabled={deciding} onClick={() => decide(true)}>Allow</Button>
        <Button size="sm" variant="secondary" disabled={deciding} onClick={() => decide(false)}>Deny</Button>
      </div>
    </div>
  );
}
